<script>
import { mapGetters, mapActions } from 'vuex'
import AppBus from '@C/bus/app-bus.vue'
import ExtensionMixin from '@C/extension-mixin.vue'
import ExtensionHelperMixin from '@C/extension-helper-mixin.vue'
// Toast is used by the extension
import { triggerEvent, toast, recodeTrack, needsExtensionUpdate } from '@/js/utils.js'
import { RoomEventTypes } from '@root/backend/databases/events'

// This is to be extended by App.vue and provides some event listeners
// that the extension depends on
export default {
  name: 'extension-app',
  mixins: [ExtensionMixin, ExtensionHelperMixin],
  computed: {
    ...mapGetters([
      'isRoom',
      'roomName',
      'accessToken',
      'roomType',
      'roomOwner',
      'roomController',
      'videoSelectors',
      'extensionVersion'
    ])
  },
  watch: {
    isRoom () {
      this.onChange('isRoom')
    },
    roomType () {
      this.onChange('roomType')
    },
    roomController () {
      this.onChange('roomController')
    },
    videoSelectors: {
      handler (v, o) {
        triggerEvent(window, 'ext:sourceChange', JSON.parse(JSON.stringify(v)))
      },
      deep: true
    }
  },
  methods: {
    ...mapActions([
      'processCaptions'
    ]),
    onChange (reason) {
      const { isRoom, roomName, roomType, roomOwner, roomController, accessToken } = this
      const { location } = window
      const data = {
        isRoom,
        reason,
        roomName,
        roomType,
        roomController,
        roomOwner,
        userToken: accessToken,
        href: JSON.parse(JSON.stringify(location))
      }
      triggerEvent(document, 'ext:is-room', data)
      triggerEvent(window, 'ext:is-room', data) // TODO: Legacy event. Remove me after v3 is stable
      triggerEvent(window, 'is-room', data) // TODO: Legacy event. Remove me
    }
  },
  beforeMount () {
    const self = this
    window.addEventListener('ext-event', async (e) => {
      let { detail } = e
      // Firefox has some security mechanisms that prevent us from passing around
      // objects from one context to another
      if (typeof detail === 'string') {
        detail = JSON.parse(detail)
      }
      const { data } = detail
      const { action } = data
      switch (action) {
        case 'inject-script': {
          const { content } = data
          window.injectScript(content)
          break
        }
        case 'generic-toast': {
          const { msg, delay } = data
          toast(msg, delay)
          break
        }
        case 'trigger-source-change': {
          const { default: Videoshare } = await import('videoshare')
          // TODO: Remove this after 20210115
          let extMediaEntry
          let onlyQueue
          let entryType
          const oldDataFormat = needsExtensionUpdate('2.1.6', this.extensionVersion)
          if (oldDataFormat) {
            ({ data: extMediaEntry } = data)
            entryType = 'media'
          } else {
            ({ data: { mediaEntry: extMediaEntry, onlyQueue } } = data)
            ;({ entryType } = extMediaEntry)
          }
          const { default: MediaEntry } = await import(/* webpackChunkName: "media-entry-base" */ '@root/backend/databases/media-entry-base')
          switch (entryType) {
            case 'media': {
              self.afterTabMediaFetch(extMediaEntry)
              const mediaEntry = new MediaEntry(self.buildMediaEntryFromExtensionData(extMediaEntry))
              if (mediaEntry.videoData.tracks) {
                const tracks = []
                await Promise.all(mediaEntry.videoData.tracks.map(async (entry) => {
                  try {
                    tracks.push(await recodeTrack(entry))
                  } catch (e) {
                    console.warn(e)
                  }
                }))
                mediaEntry.videoData.tracks = await this.processCaptions(tracks)
              }
              const { videoSelector } = mediaEntry
              if (onlyQueue) {
                // Queue this entry
                return AppBus.$emit(RoomEventTypes.ROOM_PLAYLIST_ADD, [mediaEntry])
              } else {
                // Don't change the current selector or trigger a media-update. Just trigger a source change and
                // let the backend determine what we should do
                const result = await Videoshare.onSourceChange(videoSelector, mediaEntry)
                if (!result) {
                  // Videoshare decided not to change source. It's probably the same as what we have (but didn't load properly)
                  // The user probably tried loading this video from a local tab that he opened up
                  // Get the video selector and emit the event on AppBus
                  Videoshare.doSourceChange({
                    videoSelector,
                    newMedia: mediaEntry
                  })
                }
              }
              break
            }
            case 'playlist': {
              const { playlist } = extMediaEntry
              const mediaEntries = playlist.map(x => new MediaEntry(x))
              AppBus.$emit(RoomEventTypes.ROOM_PLAYLIST_ADD, mediaEntries)
              break
            }
          }
          break
        }
      }
    })
    // window.addEventListener('message', async (msg) => {
    //   try {
    //     const { action } = msg
    //     switch (action) {
    //       case 'report-error': {
    //         debugger
    //       }
    //     }
    //   } catch (e) {
    //   }
    // })
    AppBus.$on('app-ready', () => {
      const evt = new CustomEvent('twoseven-app-ready')
      window.dispatchEvent(evt)
    })

    AppBus.$on('room-ready', () => {
      const evt = new CustomEvent('room-ready')
      window.dispatchEvent(evt)
    })

    AppBus.$on('ext:settingsChanged', settings => {
      this.$set(this.$store.getters.extensionConfig.settings, 'data', settings)
    })
  }
}
</script>
